import { z } from 'zod';

// --- Enums ---

export const tradeCommandSideSchema = z.enum(['buy', 'sell']);
export const tradeCommandOrderTypeSchema = z.enum(['market', 'limit', 'stop']);
export const tradeCommandStatusSchema = z.enum([
  'pending',
  'claimed',
  'filled',
  'rejected',
  'expired',
]);
export const tradeCommandResultStatusSchema = z.enum(['filled', 'rejected']);

// --- Create schema (web app → POST /api/ea/commands, JWT) ---

export const tradeCommandCreateSchema = z
  .object({
    symbol: z.string().min(1).max(32),
    side: tradeCommandSideSchema,
    orderType: tradeCommandOrderTypeSchema.default('market'),
    volume: z.number().positive().max(1000),
    price: z.number().positive().optional(),
    stopLoss: z.number().positive().optional(),
    takeProfit: z.number().positive().optional(),
    comment: z.string().max(120).optional(),
    /** Seconds until the command expires if no EA claims it. Bounded so a stale
     *  market order can never fire late. */
    ttlSeconds: z.number().int().min(10).max(3600).default(60),
  })
  .refine((c) => c.orderType === 'market' || c.price !== undefined, {
    message: 'price is required for limit/stop orders',
    path: ['price'],
  });

// --- Result schema (EA → POST /api/ea/commands/:id/result, API key) ---

export const tradeCommandResultSchema = z.object({
  status: tradeCommandResultStatusSchema,
  brokerTicket: z.string().max(64).optional(),
  brokerOrderId: z.string().max(64).optional(),
  filledPrice: z.number().optional(),
  errorCode: z.string().max(64).optional(),
  errorMessage: z.string().max(500).optional(),
  accountId: z.string().max(64).optional(),
});

export type TradeCommandSide = z.infer<typeof tradeCommandSideSchema>;
export type TradeCommandOrderType = z.infer<typeof tradeCommandOrderTypeSchema>;
export type TradeCommandStatus = z.infer<typeof tradeCommandStatusSchema>;
export type TradeCommandCreateInput = z.infer<typeof tradeCommandCreateSchema>;
export type TradeCommandResultInput = z.infer<typeof tradeCommandResultSchema>;
